import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Users, Search, Download, Check, MailCheck, UserX, RefreshCcw, Plus } from "lucide-react";

const STATUS_META = {
  waiting: { label: "Waiting", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  contacted: { label: "Contacted", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  converted: { label: "Converted", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  opt_out: { label: "Opt-out", cls: "bg-stone-100 text-stone-600 border-stone-200" },
};
const FILTERS = ["all", "waiting", "contacted", "converted", "opt_out"];

export default function AdminWaitlistTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", name: "", phone: "" });
  const [addMsg, setAddMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/waitlist/admin/list?${params}`);
      setRows(data.rows || []);
      setCounts(data.counts || {});
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);  // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const setRowStatus = async (id, newStatus) => {
    setBusy(id);
    try {
      await api.patch(`/waitlist/admin/${id}`, { status: newStatus });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy("");
    }
  };

  const submitAdd = async () => {
    setAddMsg("");
    try {
      await api.post("/waitlist/join", { ...addForm, source: "admin_manual" });
      setAddMsg("Added to waitlist.");
      setAddForm({ email: "", name: "", phone: "" });
      setShowAdd(false);
      load();
    } catch (e) { setAddMsg(e.response?.data?.detail || "Add failed"); }
    setTimeout(() => setAddMsg(""), 4000);
  };

  const exportCsv = () => {
    // Rely on browser to preserve the JWT via the same session — open in
    // new tab and let the api client cookie flow it through. The
    // `Authorization` header attaches via api interceptor on non-GET fetches
    // but for a raw click we&apos;ll manually POST-style via fetch instead.
    const token = localStorage.getItem("ff2d_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/admin/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "waitlist.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert("Export failed"));
  };

  return (
    <div data-testid="admin-waitlist-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Users size={20}/>
        <h2 className="font-display text-2xl">Post-500 waitlist</h2>
        <span className="text-xs text-[var(--ff-text-muted)] ml-3">
          {counts.total ?? 0} total · {counts.waiting ?? 0} waiting · {counts.contacted ?? 0} contacted · {counts.converted ?? 0} converted
        </span>
        <button
          onClick={exportCsv}
          className="ml-auto text-xs inline-flex items-center gap-1 border border-stone-300 hover:border-[var(--ff-primary)] rounded-full px-3 py-1.5 transition-colors"
          data-testid="waitlist-export"
        >
          <Download size={12}/> Export CSV
        </button>
        <button
          onClick={load}
          className="text-xs inline-flex items-center gap-1 text-[var(--ff-primary)] hover:underline"
          data-testid="waitlist-refresh"
        >
          <RefreshCcw size={12}/> Refresh
        </button>
        <button onClick={() => setShowAdd(v => !v)} className="btn-primary text-xs" data-testid="waitlist-add-toggle">
          <Plus size={12}/> Add manually
        </button>
      </div>

      {showAdd && (
        <div className="card-flat p-4 mt-4 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center" data-testid="waitlist-add-form">
          <input type="email" placeholder="email@example.com" className="input-base" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} data-testid="waitlist-add-email"/>
          <input placeholder="Name (optional)" className="input-base" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} data-testid="waitlist-add-name"/>
          <input placeholder="Phone (optional)" className="input-base" value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} data-testid="waitlist-add-phone"/>
          <button onClick={submitAdd} disabled={!addForm.email} className="btn-primary text-sm" data-testid="waitlist-add-submit">Add</button>
        </div>
      )}
      {addMsg && <p className="mt-2 text-sm text-[var(--ff-primary)]" data-testid="waitlist-add-msg">{addMsg}</p>}

      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="waitlist-filters">
        {FILTERS.map(f => {
          const active = status === f;
          const label = f === "all" ? "All" : STATUS_META[f]?.label || f;
          return (
            <button
              key={f}
              onClick={() => setStatus(f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-stone-300 text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}
              data-testid={`waitlist-filter-${f}`}
            >
              {label}
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, phone…"
            className="input-base text-xs pl-7 py-1.5"
            data-testid="waitlist-search"
          />
        </div>
      </div>

      {loading && <p className="mt-6 text-[var(--ff-text-muted)]">Loading…</p>}
      {!loading && rows.length === 0 && (
        <div className="mt-8 card-flat p-10 text-center text-[var(--ff-text-muted)]">
          No one on this waitlist bucket yet.
        </div>
      )}

      <div className="mt-4 card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">#</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Phone</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Source</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.waiting;
              return (
                <tr key={r.id} className="border-t border-stone-200 hover:bg-stone-50" data-testid={`waitlist-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono tabular-nums text-xs">{r.position}</td>
                  <td className="px-4 py-2"><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></td>
                  <td className="px-4 py-2 text-xs">{r.name || <span className="text-stone-400">—</span>}</td>
                  <td className="px-4 py-2 text-xs">{r.phone || <span className="text-stone-400">—</span>}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-[var(--ff-text-muted)]">{r.source}</td>
                  <td className="px-4 py-2 text-xs text-[var(--ff-text-muted)]">{(r.joined_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap space-x-1">
                    {r.status === "waiting" && (
                      <button onClick={() => setRowStatus(r.id, "contacted")} disabled={busy === r.id}
                        className="text-sky-700 hover:underline inline-flex items-center gap-0.5" data-testid={`waitlist-mark-contacted-${r.id}`}>
                        <MailCheck size={11}/> Mark contacted
                      </button>
                    )}
                    {r.status !== "converted" && (
                      <button onClick={() => setRowStatus(r.id, "converted")} disabled={busy === r.id}
                        className="text-emerald-700 hover:underline inline-flex items-center gap-0.5" data-testid={`waitlist-mark-converted-${r.id}`}>
                        <Check size={11}/> Converted
                      </button>
                    )}
                    {r.status !== "opt_out" && (
                      <button onClick={() => setRowStatus(r.id, "opt_out")} disabled={busy === r.id}
                        className="text-rose-700 hover:underline inline-flex items-center gap-0.5" data-testid={`waitlist-mark-optout-${r.id}`}>
                        <UserX size={11}/> Opt-out
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
